/**
 * Admin Login Page
 * 
 * Displays the "Tammy's Self Service" branding and login form.
 * Mobile-first design with large touch targets for iPhone use.
 * 
 * Story 1.2: Admin Login Page UI
 * Story 1.3: Authentication Backend
 */

import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { HiEye, HiEyeOff, HiLockClosed } from 'react-icons/hi';
import { useAuth } from '../hooks/useAuth';
import { usePageSeo } from '../../utils/seo';

export default function Login() {
  const navigate = useNavigate();
  const { login, isAuthenticated, isLoading: authLoading } = useAuth();
  
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  
  // Form state
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [rememberMe, setRememberMe] = useState(true);

  usePageSeo({
    title: "Admin Login | Nanny B's Bakery",
    description: 'Staff login for Nanny B\'s Bakery admin portal.',
    canonicalPath: '/admin/login',
    robots: 'noindex, nofollow',
  });

  // Redirect if already logged in
  useEffect(() => {
    if (isAuthenticated && !authLoading) {
      navigate('/admin');
    }
  }, [isAuthenticated, authLoading, navigate]);

  // Handle login with real authentication
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);

    const result = await login(email, password, rememberMe);
    
    if (result.success) {
      navigate('/admin');
    } else {
      setError(result.error || 'Login failed');
    }
    
    setIsLoading(false);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-900 via-purple-800 to-purple-900 flex items-center justify-center p-4">
      {/* Decorative background elements */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute -top-40 -right-40 w-80 h-80 bg-purple-600/20 rounded-full blur-3xl" />
        <div className="absolute -bottom-40 -left-40 w-80 h-80 bg-pink-600/20 rounded-full blur-3xl" />
      </div>

      {/* Login card */}
      <div className="relative w-full max-w-md">
        {/* Logo/Branding area */}
        <div className="text-center mb-8">
          {/* Cupcake icon */}
          <div className="inline-flex items-center justify-center w-20 h-20 bg-white/10 backdrop-blur rounded-2xl mb-4 shadow-lg">
            <span className="text-4xl">🧁</span>
          </div>
          
          {/* Title */}
          <h1 className="text-3xl font-bold text-white mb-2">
            Tammy's Self Service
          </h1>
          <p className="text-purple-200 text-sm">
            Nanny B's Bakery Page Self Service
          </p>
        </div>

        {/* Login form card */}
        <div className="bg-white rounded-2xl shadow-2xl p-6 sm:p-8">
          <form onSubmit={handleSubmit} className="space-y-5">
            {/* Error message */}
            {error && (
              <div className="bg-red-50 text-red-600 px-4 py-3 rounded-lg text-sm flex items-center gap-2">
                <HiLockClosed className="w-5 h-5 flex-shrink-0" />
                {error}
              </div>
            )}

            {/* Email field */}
            <div>
              <label 
                htmlFor="email" 
                className="block text-sm font-medium text-gray-700 mb-2"
              >
                Email Address
              </label>
              <input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="nannybbakedgoods@outlook.com"
                className="w-full px-4 py-3 border border-gray-300 rounded-xl
                           focus:ring-2 focus:ring-purple-500 focus:border-purple-500
                           text-gray-900 placeholder-gray-400
                           transition-colors duration-200"
                required
                autoComplete="email"
              />
            </div>

            {/* Password field */}
            <div>
              <label 
                htmlFor="password" 
                className="block text-sm font-medium text-gray-700 mb-2"
              >
                Password
              </label>
              <div className="relative">
                <input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Enter your password"
                  className="w-full px-4 py-3 pr-12 border border-gray-300 rounded-xl
                             focus:ring-2 focus:ring-purple-500 focus:border-purple-500
                             text-gray-900 placeholder-gray-400
                             transition-colors duration-200"
                  required
                  autoComplete="current-password"
                />
                {/* Show/hide password toggle */}
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 p-2
                             text-gray-400 hover:text-gray-600
                             transition-colors duration-200"
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                >
                  {showPassword ? (
                    <HiEyeOff className="w-5 h-5" />
                  ) : (
                    <HiEye className="w-5 h-5" />
                  )}
                </button>
              </div>
            </div>

            {/* Remember me checkbox */}
            <div className="flex items-center">
              <input
                id="remember"
                type="checkbox"
                checked={rememberMe}
                onChange={(e) => setRememberMe(e.target.checked)}
                className="w-5 h-5 text-purple-600 border-gray-300 rounded
                           focus:ring-purple-500 cursor-pointer"
              />
              <label 
                htmlFor="remember" 
                className="ml-3 text-sm text-gray-600 cursor-pointer"
              >
                Keep me logged in
              </label>
            </div>

            {/* Submit button */}
            <button
              type="submit"
              disabled={isLoading}
              className="w-full py-4 px-6 bg-purple-600 hover:bg-purple-700 
                         disabled:bg-purple-400 disabled:cursor-not-allowed
                         text-white font-semibold rounded-xl
                         transition-colors duration-200
                         focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-2
                         flex items-center justify-center gap-2"
            >
              {isLoading ? (
                <>
                  <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
                    <circle 
                      className="opacity-25" 
                      cx="12" cy="12" r="10" 
                      stroke="currentColor" 
                      strokeWidth="4"
                      fill="none"
                    />
                    <path 
                      className="opacity-75" 
                      fill="currentColor" 
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
                    />
                  </svg>
                  Logging in...
                </>
              ) : (
                'Log In'
              )}
            </button>
          </form>

          {/* Forgot password link */}
          <div className="mt-6 text-center">
            <button 
              type="button"
              className="text-sm text-purple-600 hover:text-purple-700
                         hover:underline transition-colors duration-200"
            >
              Forgot your password?
            </button>
          </div>
        </div>

        {/* Footer text */}
        <p className="mt-6 text-center text-purple-200 text-xs">
          Need help? Call Humberto at your convenience
        </p>
      </div>
    </div>
  );
}

